import { Injectable } from '@angular/core';
import { ITrackAuthority, TrackAuthority } from '../models/trackauthority';
import { ActionModel } from '../models/actionmodel';
import Dexie from 'dexie';
import { Source } from "../models/source.enum";
import { User } from '@csx/security';
import { ProxyUser } from '../models/proxyuser';

@Injectable()
export class LocalStorageService {

  private authorityTable: Dexie.Table<TrackAuthority, number>;
  private offlineActionQueue: Dexie.Table<ActionModel, number>;
  private userTable: Dexie.Table<User, number>;

  constructor(private user: User, private proxy: ProxyUser) {
    let dexie: Dexie = new Dexie("ATAM_INDEXDB");
    dexie.version(1).stores({ "TRACKAUTHORITY": "id", "OFFLINEACTION": "++id", "USERDETAIL": "" });
    dexie.open();
    this.authorityTable = dexie.table("TRACKAUTHORITY");
    this.offlineActionQueue = dexie.table("OFFLINEACTION");
    this.userTable = dexie.table("USERDETAIL");
  }

  getAllTrackAuthorities(): Promise<TrackAuthority[]> {
    return this.authorityTable.toArray();
  }

  getUserDetail(): Promise<User> {
    return this.userTable.get(0);
  }

  /**
   * Method to add multiple authorities
   * 1. Store is cleared of old authorities
   * 2. New authorities are added
   * @param trackAuthorities 
   */
  addAllTrackAuthorities(trackAuthorities: ITrackAuthority[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      this.mergeDirtyRecords(trackAuthorities.map((element: ITrackAuthority) => new TrackAuthority(element, (this.proxy.racf || this.user.racf))))
        .then((allAuthorities: TrackAuthority[]) => this.authorityTable.clear()
          .then(() => this.authorityTable.bulkAdd(allAuthorities)))
        .then(() => resolve())
        .catch(exception => {
          console.warn(exception);
          reject(exception);
        });
    });
  }

  private mergeDirtyRecords(trackAuthorities: TrackAuthority[]): Promise<TrackAuthority[]> {
    return this.authorityTable.toArray().then((oldAuthorities: TrackAuthority[]) => {
      oldAuthorities.forEach((oldAuthority: TrackAuthority) => {
        if (oldAuthority.dirty) {
          let trackAuthority: TrackAuthority = trackAuthorities.find((trackAuthority: TrackAuthority) => oldAuthority.id === trackAuthority.id);
          if (trackAuthority !== undefined) {
            trackAuthority.sourceSystemName = oldAuthority.sourceSystemName;
            trackAuthority.dirty = oldAuthority.dirty;
          }
        }
      });
      return trackAuthorities;
    });
  }

  enqueueOfflineAction(actionModels: ActionModel[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (actionModels && actionModels.length) {
        this.offlineActionQueue.bulkAdd(actionModels).then(() => resolve()).catch(Dexie.BulkError, exception => {
          console.warn(exception);
          reject(exception);
        });
      }
    });
  }

  storeUserDetail(userDetail: User): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (userDetail) {
        this.userTable.clear().then(() => {
          this.userTable.put(userDetail, 1).then(() => resolve()).catch(Dexie.BulkError, exception => {
            console.warn(exception);
            reject(exception);
          })
        });
      }
    });
  }

  removeUserDetail(userDetail: User): Promise<void> {

    return new Promise<void>((resolve, reject) => {
      if (userDetail) {
        this.userTable.clear().catch(Dexie.BulkError, exception => {
          console.warn(exception);
          reject(exception);
        });
      }
    });
  }

  dequeueOfflineAction(actionModel: ActionModel): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.offlineActionQueue.delete(actionModel.id).then(() => resolve()).catch(Dexie.BulkError, exception => {
        console.warn(exception);
        reject(exception);
      });
    });
  }

  getAllOfflineActions(): Promise<ActionModel[]> {
    return new Promise<ActionModel[]>((resolve, reject) => this.offlineActionQueue.toArray().then((actionModels: ActionModel[]) => {
      resolve(actionModels);
    }).catch(Dexie.BulkError, exception => {
      console.warn(exception);
      reject(exception);
    }));
  }

  getOfflineAction(trackAuhority: TrackAuthority): Promise<ActionModel> {
    return new Promise<ActionModel>((resolve, reject) => this.offlineActionQueue.toArray().then((actionModels: ActionModel[]) => {
      resolve(actionModels.find((item) => item.trackAuthority.id === trackAuhority.id));
    }).catch(Dexie.BulkError, exception => {
      console.warn(exception);
      reject(exception);
    }));
  }

  setSourceAuthoritySource(trackauthority: TrackAuthority, source: Source): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.authorityTable.update(trackauthority.id, { "sourceSystemName": source, "dirty": true }).then(() => resolve()).catch(error => reject());
    });
  }

  updateRetryCount(actionModel: ActionModel, numberOfRetry: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      return this.offlineActionQueue.update(actionModel.id, { "numberOfRetry": numberOfRetry }).then(() => resolve()).catch(error => reject());
    });
  }
}